import "@/i18n/config";
import { EventChangeArg, EventInput } from "@fullcalendar/core";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import React, { useEffect, useState } from "react";
import { MdOutlineHelpOutline } from "react-icons/md";
import { useParams } from "react-router-dom";

import { InterviewSettingHelpModal } from "@/client_app/components/InterviewSettingHelpModal";
import {
  createBooking,
  createInterviewAvailable,
  deleteInterviewAvailable,
  getInterview,
} from "@/client_app/components/services/FetchAPIService";

export function InterviewSettingUpPage() {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [data, setData] = useState();
  const [bookings, setBookings] = useState<EventInput>();
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [headerToolbar, setHeaderToolbar] = useState({
    left: "prev,next",
    center: "title",
    right: "timeGridWeek,timeGridDay",
  });

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    const updateToolbar = () => {
      if (window.innerWidth < 640) {
        // Small screen (e.g., mobile)
        setHeaderToolbar({
          left: "prev,next",
          center: "",
          right: "title",
        });
      } else {
        // Larger screens
        setHeaderToolbar({
          left: "prev,next today",
          center: "title",
          right: "timeGridWeek,timeGridDay",
        });
      }
    };

    updateToolbar(); // Initial check
    window.addEventListener("resize", updateToolbar);

    return () => window.removeEventListener("resize", updateToolbar);
  }, []);

  const updateEvents = (data) => {
    let events = data["events"];
    if (data["bookings"]) {
      setBookings(data["bookings"]);
      const bookingData = data["bookings"].map((booking) => ({
        id: booking.id,
        start: booking.start,
        end: booking.end,
        title: booking.name + " - " + booking.status,
        color: booking.status === "pending" ? "orange" : "green",
        editable: false,
      }));
      events = [...events, ...bookingData];
    }
    setEvents(events);
  };
  useEffect(() => {
    if (id) {
      getInterview(id)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              throw new Error("Interview not found");
            } else {
              throw new Error("Failed to fetch events");
            }
          }
          return res.json();
        })
        .then((data) => {
          setData(data);
          updateEvents(data);
        })
        .catch((err) => {
          setError(err.message);
        });
    }
  }, [id]);

  if (error) {
    return <h1 className='text-headline2 mb-4'>Error: {error}</h1>;
  }
  const handleSelect = (info) => {
    setEvents((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        start: info.startStr,
        end: info.endStr,
      },
    ]);
    createInterviewAvailable(id, null, info.startStr, info.endStr)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Interview not found");
          } else {
            throw new Error("Failed to fetch events");
          }
        }
        return res.json();
      })
      .then((data) => {
        updateEvents(data);
      });
  };
  const handleEventChange = (changeInfo: EventChangeArg) => {
    const { event } = changeInfo;

    const startStr = event.startStr;
    const endStr = event.endStr;

    createInterviewAvailable(id, event.id, startStr, endStr)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Interview not found");
          } else {
            throw new Error("Failed to fetch events");
          }
        }
        return res.json();
      })
      .then((data) => {
        updateEvents(data);
      });
  };

  const handleEventClick = (info) => {
    const matchedBooking = bookings.find((b) => b.id === info.event.id);

    if (matchedBooking) {
      if (matchedBooking.status === "pending") {
        if (window.confirm("Confirm this time slot?")) {
          createBooking(null, info.event.id, null, null, false, "confirmed")
            .then((res) => res.json())
            .then((data) => {
              const event = data["event"];
              setEvents((prev) => [
                ...prev,
                {
                  id: event.id,
                  start: event.start,
                  end: event.end,
                  title: event.status,
                  color: "green",
                  editable: false,
                },
              ]);
            });
        }
      }
    } else {
      if (window.confirm("Delete this time slot?")) {
        deleteInterviewAvailable(id, info.event.id).then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              throw new Error("Interview not found");
            } else {
              throw new Error("Failed to fetch events");
            }
          }
          return res.json();
        });
        setEvents((prev) => prev.filter((e) => e.id !== info.event.id));
      }
    }
  };
  if (!data) {
    return <h1 className='text-headline2 mb-4'>Loading...</h1>;
  }
  return (
    <div className='text-legacyColor-neutral-1-500'>
      <header className='py-8 bg-primary-50'>
        <h1 className='text-headline2 mb-4 px-12'>Interview Setting Up</h1>
        <div className='text-xl px-12'>{data["job_role"]}</div>
      </header>
      <div className='px-12 py-8'>
        <div className='flex justify-between'>
          <div>
            <span className='font-bold'>Interviewer:</span> {data["email"]}
          </div>

          <div>
            <span className='font-bold'>Talent representative:</span>{" "}
            <span className='capitalize'>{data["talent"]["name"]}</span> -{" "}
            {data["talent"]["email"]}
          </div>
          <button
            onClick={() => setOpenModal(true)}
            className='items-center flex justify-end'
          >
            <div className='pr-1 font-bold'>Help</div> <MdOutlineHelpOutline />{" "}
          </button>
        </div>

        <div className='text-lg pb-6'></div>
        {openModal && (
          <InterviewSettingHelpModal closeModal={() => setOpenModal(false)} />
        )}
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin]}
          timeZone='Australia/Melbourne'
          initialView='timeGridWeek'
          allDaySlot={false}
          selectable={true}
          editable={true}
          events={events}
          eventClick={handleEventClick}
          eventChange={handleEventChange}
          select={handleSelect}
          height={window.innerHeight - 250}
          headerToolbar={headerToolbar}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: "08:00",
            endTime: "18:00",
          }}
          scrollTime='08:00:00'
        />
      </div>
    </div>
  );
}
